// 모사 요약 칩 + hover 상세 팝오버 — 실데이터 칩 옆에서 "무엇을 모사 중인가"를 말한다.
//
// 결정 기록: docs/decisions/2026-09-03-composer-simulation-persist-and-badge.md (개정 절 참조)
// 2026-09-03 사용자 결정: 상시 모사 배너(단독 행)는 공간을 먹어 제거하고, 칩에 커서를 올리면
// 상세(축별 한 줄 + 전체 해제)를 보이는 것으로 §⑥("모사 중임이 계속 보인다")을 잇는다.
// 배너가 갖고 있던 「전체 해제」는 팝오버가 승계한다 — 없애면 축이 많을 때 개별 ✕ 왕복뿐이다.
//
// hover는 CSS(group-hover)가 아니라 state로 연다 — 키보드(focus)로도 열리고, spec이 행동으로
// 검증할 수 있다(jsdom은 CSS hover를 계산하지 않는다). 팝오버는 칩과 같은 래퍼 안에 두어
// 마우스가 칩→팝오버로 건너가도 닫히지 않는다(래퍼 단위 enter/leave).

import { useState } from 'react';

interface Props {
  /** 값이 실린 축의 문구("축 라벨: 선택 라벨") — EditorPage의 `simulationChips` 그대로. */
  chips: string[];
  /** 칩 클릭 — 실제 데이터 반영 모달을 연다(개별 변경·해제는 모달의 몫). */
  onOpenModal: () => void;
  /** 「전체 해제」 — 모사 값·라벨만 걷어낸다(엔티티 선택 유지). */
  onClearAll: () => void;
}

export function SimulationChip({ chips, onOpenModal, onClearAll }: Props) {
  const [open, setOpen] = useState(false);
  if (chips.length === 0) return null;

  return (
    <div
      className="relative min-w-0"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        // 래퍼 밖으로 포커스가 나갈 때만 닫는다(칩→전체 해제 버튼 이동에 닫히면 키보드로 못 누른다).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        onClick={onOpenModal}
        className="flex min-w-0 items-center gap-1 rounded-control border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 hover:bg-amber-100"
      >
        {/* 개수를 앞세운다 — 실데이터 칩(초록)과 색·라벨이 모두 갈려야 착각하지 않는다. */}
        <span className="shrink-0 font-semibold">모사 {chips.length}</span>
        <span className="max-w-[240px] truncate text-amber-700">{chips.join(' · ')}</span>
      </button>
      {open && (
        <div
          role="tooltip"
          aria-label="데이터 모사 상세"
          // 너비: "축 라벨: 값"이 가급적 한 줄에 들어가게 넉넉히(2026-09-03 사용자 요청). 극단적으로
          // 긴 값만 줄바꿈으로 받는다(max-w 초과 시 wrap이 폴백 — nowrap이면 상자를 뚫는다).
          className="absolute left-0 top-full z-40 mt-1 min-w-[280px] max-w-[36rem] rounded-card border border-amber-200 bg-white p-2 shadow-lg"
        >
          <ul className="space-y-0.5 text-xs text-amber-800">
            {chips.map((c) => (
              <li key={c} className="leading-snug">
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-amber-100 pt-1.5">
            <span className="text-[11px] text-gray-500">칩 클릭 = 개별 변경·해제</span>
            <button
              type="button"
              onClick={onClearAll}
              className="shrink-0 rounded border border-amber-300 bg-white px-2 py-0.5 text-xs text-amber-800 hover:bg-amber-100"
            >
              전체 해제
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
